import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, Star, Trash2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Review } from '@/types/review';
import { cn, getInitials } from '@/lib/utils';
import { useAuth } from '@/components/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

function formatInstagramTime(dateString: string): string {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  
  if (diffSecs < 60) return `${diffSecs}s`;
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  if (diffWeeks < 4) return `${diffWeeks}w`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface InstagramCommentCardProps {
  comment: Review;
  onLike: (id: string) => void;
  onReplyClick: (comment: Review) => void;
  onDeleteComment?: (id: string) => void;
  onDeleteReply?: (replyId: string) => void;
  isLiking: boolean;
}

export function InstagramCommentCard({
  comment,
  onLike,
  onReplyClick,
  onDeleteComment,
  onDeleteReply,
  isLiking,
}: InstagramCommentCardProps) {
  const { session } = useAuth();
  const { toast } = useToast();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if user is admin
  useEffect(() => {
    async function checkAdmin() {
      if (!session?.user?.id) return;
      
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id)
        .eq('role', 'admin')
        .maybeSingle();
      
      setIsAdmin(!!data);
    }
    
    checkAdmin();
  }, [session?.user?.id]);

  const contentCharLimit = 280;
  const shouldTruncate = comment.content.length > contentCharLimit;
  const displayContent = shouldTruncate && !isExpanded 
    ? comment.content.slice(0, contentCharLimit) + "..." 
    : comment.content;

  const replyCount = comment.replies?.length || 0;
  const displayName = comment.is_generated 
    ? (comment.anonymous_display_name || 'Anonymous') 
    : (comment.user_display_name || 'Anonymous User');
  
  const canDeleteComment = session?.user?.id === comment.user_id || isAdmin;

  const handleLikeClick = () => {
    if (!session?.user?.id) {
      toast({
        title: "Sign in required",
        description: "Please sign in to like comments",
        variant: "destructive"
      });
      return;
    }
    onLike(comment.id);
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      className="py-1.5"
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <Avatar className="w-8 h-8 flex-shrink-0">
          {comment.user_avatar_url ? (
            <AvatarImage src={comment.user_avatar_url} alt={displayName} />
          ) : (
            <AvatarFallback className="bg-primary/20 text-primary text-xs">
              {getInitials(displayName)}
            </AvatarFallback>
          )}
        </Avatar>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm font-medium text-white">
              {displayName}
            </span>
            <span className="text-xs text-white/50">
              {formatInstagramTime(comment.created_at)}
            </span>
          </div>

          {/* Star Rating Display */}
          <div className="flex items-center gap-0.5 mb-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={cn(
                  "w-3 h-3 transition-all",
                  star <= comment.rating
                    ? "fill-purple-400 text-purple-400"
                    : "fill-transparent text-white/20"
                )}
              />
            ))}
          </div>

          <p className="text-white/80 text-sm leading-relaxed mb-1">
            {displayContent}
          </p>

          {shouldTruncate && (
            <button 
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-white/50 text-xs hover:text-white/70 transition-colors"
            >
              {isExpanded ? "Show less" : "Read more"}
            </button>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-4 mt-1.5">
            <button
              onClick={() => onReplyClick(comment)}
              className="text-white/60 hover:text-white text-xs font-medium transition-colors"
            >
              Reply
            </button>
            
            {replyCount > 0 && (
              <button
                onClick={() => setShowReplies(!showReplies)}
                className="text-white/60 hover:text-white text-xs font-medium transition-colors"
              >
                {showReplies ? 'Hide' : 'View'} {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
              </button>
            )}

            {canDeleteComment && onDeleteComment && (
              <button
                onClick={() => onDeleteComment(comment.id)}
                className="text-red-400/60 hover:text-red-400 text-xs font-medium transition-colors"
              >
                Delete
              </button>
            )}
          </div>

          {/* Replies */}
          {showReplies && comment.replies && comment.replies.length > 0 && (
            <div className="mt-3 space-y-3 pl-3 border-l-2 border-white/10">
              {comment.replies.map((reply) => {
                const canDeleteReply = session?.user?.id === reply.user_id || isAdmin;
                
                return (
                  <div key={reply.id} className="flex items-start gap-2">
                    <Avatar className="w-6 h-6 flex-shrink-0">
                      {reply.user_avatar_url ? (
                        <AvatarImage src={reply.user_avatar_url} alt={reply.user_display_name || 'User'} />
                      ) : (
                        <AvatarFallback className="bg-primary/20 text-primary text-[10px]">
                          {getInitials(reply.user_display_name || 'User')}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-medium text-white">
                          {reply.is_generated 
                            ? (reply.anonymous_display_name || 'Anonymous') 
                            : (reply.user_display_name || 'Anonymous User')
                          }
                        </span>
                        <span className="text-[10px] text-white/50">
                          {formatInstagramTime(reply.created_at)}
                        </span>
                      </div>
                      <p className="text-white/80 text-xs leading-relaxed">
                        {reply.content}
                      </p>
                      {canDeleteReply && onDeleteReply && (
                        <button
                          onClick={() => onDeleteReply(reply.id)}
                          className="text-red-400/60 hover:text-red-400 text-[10px] font-medium transition-colors mt-1"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Like Button */}
        <button
          onClick={handleLikeClick}
          disabled={isLiking}
          className="flex flex-col items-center gap-0.5 flex-shrink-0 self-center"
        >
          <motion.div
            whileTap={{ scale: 0.9 }}
            animate={comment.has_user_liked ? { scale: [1, 1.2, 1] } : {}}
            transition={{ duration: 0.3 }}
          >
            <Heart
              className={cn(
                "w-4 h-4",
                comment.has_user_liked ? "fill-red-500 text-red-500" : "text-white/60"
              )}
            />
          </motion.div>
          <span className="text-xs text-white/60">{comment.likes_count || 0}</span>
        </button>
      </div>
    </motion.div>
  );
}
